import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { useOpenMemberCycle } from '../../hooks/useOpenMemberCycle';

interface OpenCycleDialogProps {
  userId: string;
  displayName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Manually opens a member's next entitlement cycle — exceptional use, e.g.
 * fixing a cycle that failed to auto-open. The Figma mockup also shows a
 * "period start date" field and a live days-preview computed from the
 * member's tenure, but `POST /time-off/members/{userId}/cycles` only takes
 * an optional `cycleNumber` (no date field), and there is no endpoint to
 * read a member's hire date to compute real tenure — both omitted/kept
 * generic rather than showing controls or numbers with no real effect.
 */
export function OpenCycleDialog({ userId, displayName, open, onOpenChange }: OpenCycleDialogProps) {
  const { t } = useTranslation('timeOff');
  const openCycle = useOpenMemberCycle();
  const name = displayName || userId;

  function handleConfirm() {
    openCycle.mutate({ userId }, { onSuccess: () => onOpenChange(false) });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="time-off-member-open-cycle-dialog">
        <DialogHeader>
          <DialogTitle>{t('team.members.openCycleDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('team.members.openCycleDialog.description', { name })}
          </DialogDescription>
        </DialogHeader>

        <p
          className="rounded-md bg-primary/5 p-3 text-xs text-primary"
          data-testid="time-off-member-open-cycle-hint"
        >
          {t('team.members.openCycleDialog.hint')}
        </p>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('team.members.openCycleDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={openCycle.isPending}
            data-testid="time-off-member-open-cycle-confirm"
          >
            {t('team.members.openCycleDialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
